import type { TFunction } from 'i18next';
import type { TaskTypeBreakdownResponse } from '../../../api/work-periods-client/work-periods-client.type';
import { DEFAULT_CHART_COLORS } from '../../financials/constants';
import { getTaskTypeText } from '../../tasks-table/tasks-table.helpers';
import type { TaskType } from '../../tasks-table/tasks-table.type';

/**
 * One renderable slice/bar of the sprint-composition breakdown chart.
 */
interface BreakdownChartEntry {
  name: string;
  count: number;
  fill: string;
}

/**
 * Builds the chart entries for the sprint-composition breakdown.
 *
 * Prefers the labeled `type_breakdown` rows when the backend provides them:
 * the verbatim external label (`raw_type_label`) is shown when it differs
 * from the stored `raw_type`, otherwise the row keeps the legacy
 * `getTaskTypeText` rendering so pre-resync data displays exactly as before.
 * When `type_breakdown` is absent (older backends), falls back to the
 * legacy `types` map path unchanged.
 *
 * @param {TaskTypeBreakdownResponse | undefined} data - Breakdown response; may be undefined while the query is loading
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {BreakdownChartEntry[]} Chart entries with display name, count, and cycling fill color
 */
const buildBreakdownChartEntries = (
  data: TaskTypeBreakdownResponse | undefined,
  t: TFunction,
): BreakdownChartEntry[] => {
  if (!data) {
    return [];
  }

  if (data.type_breakdown?.length) {
    return data.type_breakdown.map((entry, index) => ({
      name:
        entry.raw_type_label !== entry.raw_type
          ? entry.raw_type_label
          : getTaskTypeText(entry.raw_type as unknown as TaskType, null, t),
      count: entry.count,
      fill: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
    }));
  }

  // Convert raw type strings to TaskType for display
  return Object.entries(data.types).map(([type, count], index) => ({
    name: getTaskTypeText(type as unknown as TaskType, null, t),
    count,
    fill: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
  }));
};

export { buildBreakdownChartEntries };
export type { BreakdownChartEntry };
